import { PublicRoutes } from "appRoutePaths";
import jwt_decode from "jwt-decode";
import { createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ZendeskAPI } from "react-zendesk";

export enum Roles {
    User = 'user',
    Breaker = 'breaker',
    Typer = 'typer',
    Staff = 'staff',
    SeniorStaff = 'senior_staff',
    Admin = 'admin',
}

export type MfaProvider = "Email" | "Authenticator";

export interface IAuthToken {
    accessToken: string;
    tokenType: string;
    expiresIn?: number;
    refreshToken?: string;
}

export interface IAuthContext {
    authToken: string | undefined;
    authTokenFactory: () => string;
    tokenType: string | undefined;
    isInWebView: boolean;
    setToken(token: IAuthToken | undefined): void;
    startImpersonating(impersonatedToken: IAuthToken): void;
    stopImpersonating(): void;
    isImpersonating(): boolean;
    refreshToken?: string;
}

export interface Identity {
    isLoggedIn: boolean;
    accounts: string[];
    holdingAccount: string | null;
    logout: () => void;
    id?: string;
    zendeskUserId?: string;
    email?: string;
    userName?: string;
    roles: Roles[];
    isAdmin?: boolean;
    isCustomer?: boolean;
    isInRole: (roleArray: Roles[]) => boolean;
    twoFactorTokenRequired: boolean;
    twoFactorSetupRequired: boolean;
    twoFactorProvider: MfaProvider;
}

interface IJwtToken {
    "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid": string;
    "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name": string;
    "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress": string;
    "http://schemas.microsoft.com/ws/2008/06/identity/claims/role":
    | string
    | string[];
    "dcs://acnt": string | string[];
    "dcs://acnt/holding"?: string;
    "dcs://zuid": string;
    "dcs://2fa-code/required": boolean;
    "dcs://2fa-setup/required": boolean;
    "dcs://2fa/provider": MfaProvider;
    exp: number;
}

export const AuthContext = createContext<IAuthContext>({
    authToken: undefined,
    authTokenFactory: () => "",
    tokenType: undefined,
    isInWebView: false,
    setToken: () => { },
    startImpersonating: () => { },
    stopImpersonating: () => { },
    isImpersonating: () => false,
});

/**
 * Returns authentication information. Only use this in hooks that perform
 * authenticated calls to the back-end, or when setting the result of a successful
 * login. If you need information on the current user, see {@link useIdentity}.
 * @returns  An {@link IAuthContext} representing the current context
 */
export const useAuthContext = () => {
    return useContext(AuthContext);
};

const normalizeToArray = (value: string | string[]): string[] => {
    return typeof value === "string" ? [value] : value;
};

const mapRoleToEnum = (value: string | string[]): Roles[] => {
    const roles = normalizeToArray(value);
    const result: Roles[] = [];

    Object.keys(Roles).forEach(roleKey => {
        const roleName = roleKey as keyof typeof Roles;
        const roleValue = Roles[roleName];

        if (roles.includes(roleValue)) {
            result.push(roleValue);
        }
    });

    return result;
};

/**
 * Obtains information about the current user, if any. Use the
 * {@link Identity.isLoggedIn} property of the return value to verify if a user is present.
 * @returns The {@link Identity} of the current user
 */
export const useIdentity = (): Identity => {
    const { authToken, setToken } = useAuthContext();

    const navigate = useNavigate();
    const logout = () => {
        setToken(undefined);
        navigate(PublicRoutes.login.url);

        // Also log out of the zendesk chat widget
        ZendeskAPI("webWidget", "hide");
        ZendeskAPI("webWidget", "logout");
    };

    if (authToken === undefined) {
        return {
            isLoggedIn: false,
            roles: [],
            accounts: [],
            holdingAccount: null,
            twoFactorTokenRequired: false,
            twoFactorSetupRequired: false,
            twoFactorProvider: "Authenticator",
            isInRole: () => false,
            logout,
        };
    }

    const decoded = jwt_decode<IJwtToken>(authToken);

    const {
        "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid": id,
        "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name": userName,
        "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress":
        email,
        "http://schemas.microsoft.com/ws/2008/06/identity/claims/role": role,
        "dcs://acnt": account,
        "dcs://acnt/holding": holdingAccount,
        "dcs://zuid": zendeskUserId,
        "dcs://2fa-code/required": twoFactorTokenRequired,
        "dcs://2fa-setup/required": twoFactorSetupRequired,
        "dcs://2fa/provider": twoFactorProvider = "Authenticator",
        exp: expirationSeconds,
    } = decoded;

    const isLoggedIn = Date.now() / 1000 < expirationSeconds;
    // roles and accounts will come back as a string if there are
    // only one, and an array if there are many. normalize this to
    // always be an array.    
    const roles = mapRoleToEnum(role);
    const accounts = normalizeToArray(account);
    const isAdmin = roles.includes(Roles.Admin);
    const isCustomer = roles.includes(Roles.User) || roles.includes(Roles.Breaker);

    const isInRole = (roleArray: Roles[]) =>
        roleArray.some(r => roles.includes(r));

    return {
        isLoggedIn,
        id,
        zendeskUserId,
        email,
        userName,
        roles,
        accounts,
        holdingAccount: holdingAccount ?? null,
        isAdmin,
        isCustomer,
        isInRole,
        twoFactorTokenRequired,
        twoFactorSetupRequired,
        twoFactorProvider,
        logout,
    };
};